import React, { useEffect, type FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { Space } from '@g2u/ui-kit';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Badge, Spin, Tabs, Tag } from 'antd';

import { RootStore } from 'store';
import { Content } from 'components';
import { Condition } from 'types';
import { technicianFileTabs } from 'data';
import {
  BlacklistTechnician,
  TechnicianDetails,
  TechnicianJobs,
  TechnicianNotes,
  TechnicianTickets,
} from './components';
import { useTechnician } from 'helpers/hooks/useTechnician';
import styles from './edit.module.scss';

export const TechnicianEditPage: FC = observer(() => {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = RootStore;

  const { technician, isLoading, updateTechnician, blacklistTechnician } = useTechnician(id);
  const isBlacklisted = technician?.blacklist === Condition.Yes;

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/technician/search',
        title: 'Find a technician',
      },
      {
        title: `Technician File ${id}`,
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, [id]);

  const handleChangeTab = (tabId: string) => {
    navigate(`/technician/edit/${id}/${tabId}`);
  };

  const mapTabs = (tabId: string) => {
    switch (tabId) {
      case 'details':
        return <TechnicianDetails.$ technician={technician!} onUpdate={updateTechnician} />;
      case 'jobs':
        return <TechnicianJobs.$ technicianId={id} />;
      case 'notes':
        return <TechnicianNotes.$ technicianId={id} />;
      case 'tickets':
        return <TechnicianTickets.$ technicianId={id} />;
      default:
        return <></>;
    }
  };
  return (
    <Content.$ dataTestId="create-customer-page" className={styles.edit}>
      <Helmet>
        <title>Edit Technician</title>
      </Helmet>
      <Badge.Ribbon
        text={
          <>
            Technician: {technician?.contact1_title} {technician?.contact1_firstname} {technician?.contact1_surname}{' '}
            {isBlacklisted ? '| Blacklisted' : ''}
          </>
        }
        color={isBlacklisted ? 'red' : 'blue'}
        placement="start"
        style={{ top: 12 }}
      >
        <Tag className={styles.edit__notes} color="red">
          {technician?.notes}
        </Tag>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large} className={styles.edit__container}>
          <Tabs
            activeKey={tab}
            onChange={handleChangeTab}
            defaultActiveKey={tab ?? 'details'}
            tabPosition="top"
            style={{ minHeight: 220 }}
            items={technicianFileTabs.map((tab) => {
              return {
                label: tab.title,
                key: tab.id,
                children:
                  !technician && isLoading ? (
                    <Space.$ justify={Space.Justify.Center}>
                      <Spin style={{ margin: '0 auto', padding: 40 }} />
                    </Space.$>
                  ) : (
                    mapTabs(tab.id)
                  ),
              };
            })}
          />
        </Space.$>
      </Badge.Ribbon>
      <Space.$ align={Space.Align.End} direction={Space.Direction.Vertical}>
        <BlacklistTechnician.$ isBlacklisted={technician?.blacklist === Condition.Yes} onAdd={blacklistTechnician} />
      </Space.$>
    </Content.$>
  );
});
